import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { ChipGroup } from "../controls";
import FoodPreferencesService from "./FoodPreferencesService";
import IngredientAutocomplete from "./IngredientAutocomplete";
import { FP_V2_REFRESH, FP_V2_AI_INJECT } from "./events";
import { FOOD_BEHAVIOR_FIELDS, UNSAVED_WARN } from "./data";

/**
 * Food preferences v2 — content-only section, backed by
 * api-food-preferences-v2-service. Single-doc-per-patient with an explicit
 * Save button (no auto-save). Two cards:
 *
 *   1. Dietary pattern — multi-select Diet type, conditional Non-veg
 *      sub-options (shown when "non-vegetarian" is picked), multi-select
 *      Cuisine preferences. All options + the diet-type mutex group come
 *      from `/config` (backend is source of truth).
 *
 *   2. Food behavior — four search-driven chip lists (Likes, Dislikes,
 *      Restrictions, Limitations) all backed by the federated ingredient
 *      catalog (api-recipes-service). Cross-field uniqueness: an ingredient
 *      can be in at most one list per patient — enforced client-side (dropdown
 *      greys out already-selected names) and server-side (400 on violation).
 *      Plus a free-text Note field.
 *
 * Save button is enabled only when there are unsaved changes vs. the last
 * server snapshot. A window `beforeunload` handler warns the user if they
 * try to navigate away with pending edits.
 */

// Note word cap — same 250-word ceiling and clamp-on-input behaviour as the
// Description field in Family history's relative modal (and Diagnosis /
// Hospitalisation): keystrokes past the limit are ignored rather than warned
// about after the fact.
const MAX_NOTE_WORDS = 250;
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

// Session cache for /config so tab switches don't re-fetch.
let CONFIG_CACHE = null;

const emptyDietaryPattern = () => ({
  dietTypes: [],
  nonVegSubOptions: [],
  cuisinePreferences: [],
});

const emptyFoodBehavior = () => ({
  likes: [],
  dislikes: [],
  restrictions: [],
  limitations: [],
  note: "",
});

const arraysEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
};

const chipListEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ax = a[i] || {};
    const bx = b[i] || {};
    if ((ax.name || "") !== (bx.name || "")) return false;
    if ((ax.catalogId || "") !== (bx.catalogId || "")) return false;
    if ((ax.source || "") !== (bx.source || "")) return false;
  }
  return true;
};

// Full local ↔ server-snapshot equality — used to compute the dirty state.
const isDocEqual = (local, snap) => {
  const ldp = local.dietary;
  const sdp = snap.dietary;
  if (!arraysEqual(ldp.dietTypes, sdp.dietTypes)) return false;
  if (!arraysEqual(ldp.nonVegSubOptions, sdp.nonVegSubOptions)) return false;
  if (!chipListEqual(ldp.cuisinePreferences, sdp.cuisinePreferences)) return false;
  const lfb = local.behavior;
  const sfb = snap.behavior;
  if (!chipListEqual(lfb.likes, sfb.likes)) return false;
  if (!chipListEqual(lfb.dislikes, sfb.dislikes)) return false;
  if (!chipListEqual(lfb.restrictions, sfb.restrictions)) return false;
  if (!chipListEqual(lfb.limitations, sfb.limitations)) return false;
  if ((lfb.note || "") !== (sfb.note || "")) return false;
  return true;
};

// Serialise local state to the PUT body shape.
const buildPutBody = (dietary, behavior, version) => ({
  dietaryPattern: {
    dietTypes: dietary.dietTypes || [],
    nonVegSubOptions: dietary.nonVegSubOptions || [],
    cuisinePreferences: dietary.cuisinePreferences || [],
  },
  foodBehavior: {
    likes: behavior.likes || [],
    dislikes: behavior.dislikes || [],
    restrictions: behavior.restrictions || [],
    limitations: behavior.limitations || [],
    note: behavior.note || "",
  },
  expectedVersion: version || 0,
});

// Adopt the server response into local editable state.
const adopt = (resp) => ({
  version: (resp && resp.version) || 0,
  dietaryPattern: {
    dietTypes: (resp && resp.dietaryPattern && resp.dietaryPattern.dietTypes) || [],
    nonVegSubOptions:
      (resp && resp.dietaryPattern && resp.dietaryPattern.nonVegSubOptions) || [],
    cuisinePreferences:
      (resp && resp.dietaryPattern && resp.dietaryPattern.cuisinePreferences) || [],
  },
  foodBehavior: {
    likes: (resp && resp.foodBehavior && resp.foodBehavior.likes) || [],
    dislikes: (resp && resp.foodBehavior && resp.foodBehavior.dislikes) || [],
    restrictions: (resp && resp.foodBehavior && resp.foodBehavior.restrictions) || [],
    limitations: (resp && resp.foodBehavior && resp.foodBehavior.limitations) || [],
    note: (resp && resp.foodBehavior && resp.foodBehavior.note) || "",
  },
});

const FoodPreference = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const [config, setConfig] = useState(CONFIG_CACHE);
  const [configError, setConfigError] = useState(null);
  const [version, setVersion] = useState(0);
  const [dietary, setDietary] = useState(emptyDietaryPattern());
  const [behavior, setBehavior] = useState(emptyFoodBehavior());
  // Server snapshot — what the local state was at load-time / last save.
  // Used to compute the dirty flag driving Save button + beforeunload warn.
  const [snapshot, setSnapshot] = useState({
    dietary: emptyDietaryPattern(),
    behavior: emptyFoodBehavior(),
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | error | conflict
  const [saveError, setSaveError] = useState(null);

  const serviceRef = useRef(null);
  if (!serviceRef.current) serviceRef.current = new FoodPreferencesService();

  // ── /config (once per session) ─────────────────────────────────────
  useEffect(() => {
    if (config) return;
    serviceRef.current.getConfig(
      (res) => {
        CONFIG_CACHE = res || {};
        setConfig(CONFIG_CACHE);
      },
      (err) => setConfigError((err && err.message) || "Failed to load options")
    );
  }, [config]);

  // ── initial doc load (per-patient) ─────────────────────────────────
  const load = useCallback(() => {
    if (!patientId) return;
    setLoading(true);
    setLoadError(null);
    serviceRef.current.get(
      patientId,
      (res) => {
        const next = adopt(res);
        setVersion(next.version);
        setDietary(next.dietaryPattern);
        setBehavior(next.foodBehavior);
        setSnapshot({
          dietary: next.dietaryPattern,
          behavior: next.foodBehavior,
        });
        setSaveState("idle");
        setLoading(false);
      },
      (err) => {
        setLoadError((err && err.message) || "Failed to load food preferences");
        setLoading(false);
      }
    );
  }, [patientId]);

  useEffect(() => {
    load();
    EventService.addListener(FP_V2_REFRESH, load);
    return () => EventService.removeListener(FP_V2_REFRESH, load);
  }, [load]);

  // Transcript-flow bridge: merge an AI food-preference capture directly
  // into the local behavior state (Likes / Dislikes / Restrictions /
  // Limitations chip list). Marks the form dirty, exactly as if the
  // reviewer had typed the ingredient in manually — user still hits
  // the form's own Save button to persist. Payload:
  //   { id, kind: "likes"|"dislikes"|"restrictions"|"limitations", ingredient }
  // Dedup by captured-item id (the sender re-emits on tab activation so
  // late-mounting forms catch up; we ignore anything already applied).
  // If the user manually removes an injected chip, the dedup Set keeps
  // us from re-adding it on the next re-emit.
  const injectedIdsRef = useRef(new Set());
  useEffect(() => {
    const KIND_MAP = {
      like: "likes",
      likes: "likes",
      dislike: "dislikes",
      dislikes: "dislikes",
      restriction: "restrictions",
      restrictions: "restrictions",
      limitation: "limitations",
      limitations: "limitations",
    };
    const inject = (payload) => {
      const id         = payload && payload.id;
      const ingredient = ((payload && payload.ingredient) || "").trim();
      const rawKind    = ((payload && payload.kind) || "").toString().toLowerCase();
      const key        = KIND_MAP[rawKind];
      if (!ingredient || !key) return;
      if (id && injectedIdsRef.current.has(id)) return;
      if (id) injectedIdsRef.current.add(id);
      setBehavior((prev) => {
        const list = Array.isArray(prev[key]) ? prev[key] : [];
        if (list.some((x) => (x || "").toLowerCase() === ingredient.toLowerCase())) {
          return prev;
        }
        return { ...prev, [key]: [...list, ingredient] };
      });
    };
    EventService.addListener(FP_V2_AI_INJECT, inject);
    return () => EventService.removeListener(FP_V2_AI_INJECT, inject);
  }, []);

  // ── dirty tracking ────────────────────────────────────────────────
  const dirty = useMemo(
    () => !isDocEqual({ dietary, behavior }, snapshot),
    [dietary, behavior, snapshot]
  );

  // beforeunload warn when there are unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = UNSAVED_WARN;
      return UNSAVED_WARN;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ── save (explicit, no debounce) ──────────────────────────────────
  const handleSave = () => {
    if (!patientId || !dirty || saveState === "saving") return;
    setSaveState("saving");
    setSaveError(null);
    const body = buildPutBody(dietary, behavior, version);
    serviceRef.current.put(
      patientId,
      body,
      (res) => {
        const next = adopt(res);
        setVersion(next.version);
        setDietary(next.dietaryPattern);
        setBehavior(next.foodBehavior);
        setSnapshot({
          dietary: next.dietaryPattern,
          behavior: next.foodBehavior,
        });
        setSaveState("saved");
        EventService.emit(FP_V2_REFRESH);
      },
      (err) => {
        if (err && err.reason === "version") {
          setSaveState("conflict");
          setSaveError("Another edit was saved first — reloaded latest.");
          load();
          return;
        }
        setSaveState("error");
        setSaveError((err && err.message) || "Save failed");
      }
    );
  };

  // ── diet-type change ──────────────────────────────────────────────
  // Plain multi-select — any combination is legal. Only side-effect: clear
  // Non-veg sub-options when "non-vegetarian" is no longer among the picks
  // (mirrors the server-side clearing in transformer.ApplyPut).
  const applyDietTypeChange = (nextList) => {
    setDietary((prev) => {
      const next = { ...prev, dietTypes: nextList || [] };
      if (!next.dietTypes.includes("non-vegetarian")) {
        next.nonVegSubOptions = [];
      }
      return next;
    });
  };

  // ── food-behavior chip helpers ────────────────────────────────────
  // Per-field "already taken" set:
  //   • Likes / Dislikes are mutually exclusive with each other, so both
  //     autocompletes share the union of their lowercase names.
  //   • Restrictions and Limitations are independent — each only excludes
  //     its own current picks (internal dedup, no cross-list constraint).
  const disabledKeysFor = useMemo(() => {
    const toKeys = (list) => {
      const s = new Set();
      for (const item of list || []) {
        const key = (item.name || "").trim().toLowerCase();
        if (key) s.add(key);
      }
      return s;
    };
    const likesKeys = toKeys(behavior.likes);
    const dislikesKeys = toKeys(behavior.dislikes);
    const likesOrDislikes = new Set([...likesKeys, ...dislikesKeys]);
    return {
      likes: likesOrDislikes,
      dislikes: likesOrDislikes,
      restrictions: toKeys(behavior.restrictions),
      limitations: toKeys(behavior.limitations),
    };
  }, [behavior]);

  const addChip = (fieldKey, item) => {
    const key = (item.name || "").trim().toLowerCase();
    if (!key) return;
    // Silent no-op — dropdown should have blocked. Guarded here in case the
    // user hits an autocomplete without a fresh disabledKeys snapshot.
    if (disabledKeysFor[fieldKey] && disabledKeysFor[fieldKey].has(key)) return;
    setBehavior((prev) => ({ ...prev, [fieldKey]: [...(prev[fieldKey] || []), item] }));
  };

  const removeChip = (fieldKey, idx) => {
    setBehavior((prev) => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== idx),
    }));
  };

  // A note stored before this cap existed can already exceed it; clamping
  // that on the next keystroke would silently drop its tail, so the cap is
  // switched off for a note that arrived over the limit (clampWords treats
  // Infinity as "no cap"). Derived from the server snapshot, so it re-arms
  // after a save brings the note back under 250.
  const noteLimit =
    countWords(snapshot.behavior && snapshot.behavior.note) > MAX_NOTE_WORDS
      ? Infinity
      : MAX_NOTE_WORDS;

  const updateNote = (val) => {
    setBehavior((prev) => ({ ...prev, note: clampWords(val, noteLimit) }));
  };

  // ── config-derived chip options ───────────────────────────────────
  const dietTypeOptions = useMemo(
    () =>
      config && config.dietTypes
        ? config.dietTypes.map((e) => ({ value: e.key, label: e.label }))
        : [],
    [config]
  );
  const nonVegOptions = useMemo(
    () =>
      config && config.nonVegOptions
        ? config.nonVegOptions.map((e) => ({ value: e.key, label: e.label }))
        : [],
    [config]
  );
  // Cuisines are federated from the bridge catalog (bridge /v1/cuisines,
  // proxied through /config). Each option carries the catalog id as its
  // value so the selected list can be serialised back as [{name, catalogId}].
  const cuisineOptions = useMemo(
    () =>
      config && config.cuisinePreferences
        ? config.cuisinePreferences
            .map((e) => ({ value: e.catalogId || "", label: e.name || "" }))
            .filter((o) => o.value && o.label)
        : [],
    [config]
  );

  if (!patientId) {
    return <div className="ph-slot-empty"><p>Waiting for patient…</p></div>;
  }
  if (loading) {
    return <div className="ph-slot-empty"><p>Loading food preferences…</p></div>;
  }
  if (loadError) {
    return (
      <div className="ph-slot-empty">
        <p>{loadError}</p>
        <button type="button" className="sec-btn sec-btn-ghost" onClick={load}>Retry</button>
      </div>
    );
  }

  const showNonVeg = (dietary.dietTypes || []).includes("non-vegetarian");
  // Non-veg sub-options are required whenever "Non-vegetarian" is one of
  // the picked diet types — an empty sub-list would be a meaningless save
  // (server can't tell what the patient actually eats). Blocks the Save
  // button and surfaces an inline hint under the sub-option chip strip.
  const nonVegMissing =
    showNonVeg && (dietary.nonVegSubOptions || []).length === 0;
  const canSave = dirty && saveState !== "saving" && !nonVegMissing;

  return (
    <div className="sec-stack">
      {/* Card 1 — Dietary pattern */}
      <div className="lcard fp3">
        <header className="lcard-head">
          <h3 className="lcard-title">Dietary pattern</h3>
          <span className="fp-head-actions">
            {saveState === "saving" && <span className="fp-save-tag is-saving">Saving…</span>}
            {saveState === "saved" && !dirty && <span className="fp-save-tag is-saved">Saved</span>}
            {saveState === "conflict" && (
              <span className="fp-save-tag is-warn">{saveError}</span>
            )}
            {saveState === "error" && (
              <span className="fp-save-tag is-error">{saveError || "Save failed"}</span>
            )}
            {configError && (
              <span className="fp-save-tag is-error">{configError}</span>
            )}
            <button
              type="button"
              className="sec-btn sec-btn-ghost fp-save-btn"
              onClick={load}
              disabled={!canSave}
              title="Discard changes and reload from server"
            >
              Cancel
            </button>
            <button
              type="button"
              className="sec-btn sec-btn-primary fp-save-btn"
              onClick={handleSave}
              disabled={!canSave}
            >
              {saveState === "saving" ? "Saving…" : "Save"}
            </button>
          </span>
        </header>

        <div className="lfield">
          <div className="lfield-head">
            <span className="lfield-label">Diet type <span className="lfield-note">(multi)</span></span>
          </div>
          <ChipGroup
            multi
            options={dietTypeOptions}
            value={dietary.dietTypes || []}
            onChange={(v) => applyDietTypeChange(v || [])}
          />
        </div>

        {showNonVeg && (
          <div className="lfield fp-non-veg-strip">
            <div className="lfield-head">
              {/* Upstream's required marker kept; the "N SELECTED" counter
                  stays dropped — the 3416:2847 board has no count on the
                  dietary heads (verified in verify-fp.js). */}
              <span className="lfield-label">
                Non-veg sub-options{" "}
                <span className="lfield-note">(multi)</span>
                <span className="sec-label-required"> *</span>
              </span>
            </div>
            <ChipGroup
              multi
              options={nonVegOptions}
              value={dietary.nonVegSubOptions || []}
              onChange={(v) => setDietary((prev) => ({ ...prev, nonVegSubOptions: v || [] }))}
            />
            {nonVegMissing && (
              <p className="fam-field-error">
                Pick at least one non-veg sub-option when Non-vegetarian is selected.
              </p>
            )}
          </div>
        )}

        <div className="lfield">
          <div className="lfield-head">
            <span className="lfield-label">Cuisine preferences <span className="lfield-note">(multi)</span></span>
          </div>
          <ChipGroup
            multi
            options={cuisineOptions}
            value={(dietary.cuisinePreferences || []).map((cp) => cp.catalogId).filter(Boolean)}
            onChange={(ids) => {
              const nameById = new Map(cuisineOptions.map((o) => [o.value, o.label]));
              const next = (ids || [])
                .map((id) => ({ name: nameById.get(id) || "", catalogId: id }))
                .filter((cp) => cp.name && cp.catalogId);
              setDietary((prev) => ({ ...prev, cuisinePreferences: next }));
            }}
          />
        </div>

        {/* Food behavior — same card, second section (single Save applies to both). */}
        <h3 className="lcard-title fp-section-title">Food behavior</h3>

        {FOOD_BEHAVIOR_FIELDS.map((f) => {
          const list = behavior[f.key] || [];
          return (
            <div className="lfield" key={f.key}>
              <div className="fp-fhead">
                <span className="fp-fhead-label">{f.label}</span>
                <span className="fp-fhead-rule" aria-hidden="true" />
                <span className="fp-fhead-meta">{list.length} SELECTED</span>
              </div>

              <IngredientAutocomplete
                placeholder={f.placeholder}
                onPick={(item) => addChip(f.key, item)}
                disabledKeys={disabledKeysFor[f.key]}
              />

              {list.length > 0 && (
                <div className="fp-tokens">
                  {list.map((t, i) => (
                    <span className="fp-token" key={`${t.name}-${i}`}>
                      <svg
                        className="fp-token-check"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        aria-hidden="true"
                      >
                        <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{t.name}</span>
                      <button
                        type="button"
                        className="fp-token-remove"
                        onClick={() => removeChip(f.key, i)}
                        aria-label={`Remove ${t.name}`}
                      >
                        <svg
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          aria-hidden="true"
                        >
                          <path d="M6 6l8 8M14 6l-8 8" strokeLinecap="round" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="fp-group">
          <h4 className="fp-subhead">
            Note <span className="fp-subhead-note">· optional</span>
            <span className="dx-word-hint">
              {countWords(behavior.note)} / {MAX_NOTE_WORDS} words
            </span>
          </h4>
          <textarea
            className="sec-textarea"
            rows={3}
            value={behavior.note || ""}
            onChange={(e) => updateNote(e.target.value)}
            aria-label="Note"
          />
        </div>
      </div>
    </div>
  );
};

FoodPreference.propTypes = {
  patient: PropTypes.object,
};

export default FoodPreference;
